'use client';

import { useEffect } from 'react';
import { logRouteError } from '@utils/logger/logger';
import { isStaleBuildError, reloadOnceForStaleBuild } from '@utils/stale-build/staleBuild';

import 'styles/globals.css';

interface GlobalErrorProps {
  error: Error & { digest?: string };
}

/**
 * Last-resort boundary: only renders when the root or locale layout itself throws, so it replaces
 * the whole document and has no providers (no i18n, store or session). Kept dependency-free and
 * bilingual (RO + EN) for that reason. Route-level errors are handled by `app/[locale]/error.tsx`.
 *
 * Like the route boundary, a deploy-related error (see `isStaleBuildError`) triggers one automatic
 * reload, and "Try again" is a full reload rather than `reset()`.
 */
export default function GlobalError({ error }: GlobalErrorProps) {
  useEffect(() => {
    logRouteError('GlobalError', error);
    if (isStaleBuildError(error)) {
      reloadOnceForStaleBuild();
    }
  }, [error]);

  return (
    <html lang="ro">
      <body className="flex min-h-screen items-center justify-center bg-surface-100 px-4">
        <div className="w-full max-w-md rounded-md bg-surface p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold text-ink">Ne pare rău, a apărut o eroare.</h1>
          <p className="mt-1 text-sm text-ink-medium">Sorry, something went wrong.</p>
          <div className="mt-6 flex justify-center gap-2">
            <button type="button" onClick={() => window.location.reload()} className="rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-white">
              Încearcă din nou / Try again
            </button>
            {/* Full reload on purpose: the app shell is broken, so a client-side navigation can't be trusted. */}
            <button
              type="button"
              onClick={() => { window.location.href = '/'; }}
              className="rounded-full border px-4 py-2 text-sm font-semibold text-ink-medium"
            >
              Acasă / Home
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
